<template>
  <div class="sp-shell">
    <div v-if="!hasData" class="sp-empty">{{ $t('zones.patternMatrix.empty') }}</div>
    <template v-else>
      <p class="sp-caption">
        <i18n-t keypath="zones.strategic.caption" scope="global">
          <template #title><strong>{{ $t('zones.strategic.title') }}</strong></template>
        </i18n-t>
      </p>
      <PrioChart
        :option="option"
        :active="isActive"
        :height="410"
        name="StrategicPositioningChart"
      />
      <!-- The chart's own legend — hidden where a richer role key sits beside it
           (Zones tab), shown when the chart stands alone (production). -->
      <ul v-if="!hideLegend" class="chart-legend">
        <li v-for="it in legendItems" :key="it.label">
          <span class="legend-dot" :style="{ background: it.color }"></span>{{ it.label }}
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue'
import PrioChart from './ui/PrioChart.vue'
import {
  composeStrategicPositioning,
  QUADRANT_META,
} from '@/prioritization/strategicPositioning'

import { use } from 'echarts/core'
import { ScatterChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  MarkLineComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatEuro, formatPercent } from '@/i18n/format'
use([
  ScatterChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  MarkLineComponent,
  CanvasRenderer,
])

export default {
  name: 'StrategicPositioningChart',
  components: { PrioChart },
  props: {
    fixedWidgetsData: { type: Object, default: () => ({}) },
    segmentKey:       { type: String, default: 'default' },
    isActive:         { type: Boolean, default: true },
    // Per-zone sector overrides from DetectionZoneSettings; forwarded
    // to the composer so each zone is placed on the quadrant chart
    // against its own resolved ceiling (sector-override → company sector).
    zoneOverrides:    { type: Object, default: () => ({}) },
    // Hide the chart's own dot-legend when a richer legend sits beside it
    // (the Zones tab's role key). Default false → standalone chart keeps it.
    hideLegend:       { type: Boolean, default: false },
  },
  setup(props) {
    const { t, te } = useI18n()
    // English keeps the module's own text and formats exactly as before; Dutch shows
    // the quadrant/pattern names from focus.quadrant.* / focus.pattern.* and format.js.
    const isEn = () => currentLanguage() === 'en'
    const quadrantText = (key, field) => (isEn() ? QUADRANT_META[key][field] : t(`focus.quadrant.${key}.${field}`))
    const positioning = computed(() =>
      composeStrategicPositioning(props.fixedWidgetsData, props.segmentKey, props.zoneOverrides)
    )

    const hasData = computed(() => positioning.value.hasData)

    function fmtMoney(v) {
      if (!isEn()) return formatEuro(Number.isFinite(v) ? v : 0, { decimals: Number.isFinite(v) && v < 100 ? 2 : 0 })
      if (!Number.isFinite(v)) return '€0'
      return v >= 100
        ? `€${Math.round(v).toLocaleString()}`
        : `€${v.toFixed(2)}`
    }
    function fmtPct(v, digits = 1) {
      if (!Number.isFinite(v)) return '—'
      if (!isEn()) return formatPercent(v * 100, { decimals: digits })
      return `${(v * 100).toFixed(digits)}%`
    }

    // Legend label transform — strip the leading emoji + space from
    // each QUADRANT_META.legend string. The icons (⭐ 🏃 💡 🧊) work
    // well in the rest of the platform (tooltips, role chips on Prio
    // cards) but cluttered the legend strip at the bottom of the
    // chart.
    const legendText = (key) =>
      quadrantText(key, 'legend').replace(/^\S+\s/, '')

    // One scatter series per quadrant (gives a coloured legend + filterable
    // groups). Each point carries the full row in `[x, y, visitors, payload]`.
    const series = computed(() => {
      const groups = {}
      for (const key of Object.keys(QUADRANT_META)) groups[key] = []
      for (const p of positioning.value.points) groups[p.quadrant].push(p)

      return Object.keys(QUADRANT_META).map(key => ({
        // Series name must match the legend entry — both use the
        // emoji-stripped form so they link correctly in echarts.
        name: legendText(key),
        type: 'scatter',
        // sqrt scaling keeps small/medium zones visible when one zone
        // dwarfs the rest on raw visitor count.
        symbolSize: d => Math.max(12, Math.min(46, Math.sqrt(d[2]) * 0.7)),
        itemStyle: {
          color: QUADRANT_META[key].color,
          borderColor: '#fff',
          borderWidth: 1.5,
          opacity: 0.92,
        },
        emphasis: {
          itemStyle: { borderColor: '#1a2a36', borderWidth: 2, opacity: 1 },
          label: { show: true, formatter: (p) => p.data[3].zone, position: 'top' },
        },
        label: {
          show: true,
          formatter: (p) => p.data[3].zone,
          position: 'top',
          color: '#3c4b59',
          fontSize: 11,
          fontWeight: 600,
        },
        data: groups[key].map(p => [
          +p.visitorShare.toFixed(1),
          +p.eurPerVisitor.toFixed(2),
          p.visitors,
          p,
        ]),
      }))
    })

    // Quadrant divider lines — at the medians of the current store.
    const medianGuides = computed(() => ({
      name: '__medians__',
      type: 'scatter',
      data: [],
      silent: true,
      tooltip: { show: false },
      symbolSize: 0,
      z: 2,
      markLine: {
        symbol: 'none',
        lineStyle: { color: '#9aa7b3', type: 'dashed', width: 1.2 },
        label: {
          show: true,
          position: 'end',
          color: '#6b7a87',
          fontSize: 10,
          formatter: (p) => {
            if (p.data && typeof p.data.xAxis === 'number') return t('zones.strategic.medianShare')
            if (p.data && typeof p.data.yAxis === 'number') return t('zones.strategic.medianEur')
            return ''
          },
        },
        data: [
          { xAxis: positioning.value.medians.x, name: t('zones.strategic.medianShare') },
          { yAxis: positioning.value.medians.y, name: t('zones.strategic.medianEur') },
        ],
      },
    }))

    // Axis ticks keep the decimals they have (0.5 -> "0,5"), nothing more.
    const decimalsOf = (v) => (String(v).split('.')[1] || '').length

    // Axis maxima — round up nicely so the dots aren't pinned to the edges.
    function niceMax(values, padFactor = 1.15) {
      const m = values.length ? Math.max(...values) : 1
      const padded = m * padFactor
      if (padded <= 1) return Math.ceil(padded * 10) / 10
      if (padded <= 10) return Math.ceil(padded)
      if (padded <= 100) return Math.ceil(padded / 5) * 5
      return Math.ceil(padded / 10) * 10
    }
    const xMax = computed(() =>
      niceMax(positioning.value.points.map(p => p.visitorShare))
    )
    const yMax = computed(() =>
      niceMax(positioning.value.points.map(p => p.eurPerVisitor))
    )

    // Legend is rendered as HTML below the chart (see template) so it can wrap
    // tight under the x-axis with no reserved-space gap, and the card height is
    // driven by content (then equalised with its sibling via flexbox).
    const legendItems = computed(() => Object.keys(QUADRANT_META).map(k => ({
      label: legendText(k),
      color: QUADRANT_META[k].color,
    })))

    const option = computed(() => ({
      // Title + subtext intentionally dropped — the caption paragraph
      // above the chart already carries the same information.
      // Removing the in-chart title gives the plot area more
      // vertical room.

      // Grid padding mirrors PatternMatrix so the two charts share
      // identical plot-area dimensions when shown side-by-side.
      grid: {
        top: 24,
        left: 60,
        right: 30,
        bottom: 56,            /* room for the x-axis name only; legend is HTML */
        containLabel: true,
      },
      tooltip: {
        trigger: 'item',
        confine: true,
        appendToBody: false,
        formatter: (p) => {
          const row = p.data?.[3]
          if (!row) return ''
          const meta = QUADRANT_META[row.quadrant]
          const patternKey = `focus.pattern.${row.patternKind}.legend`
          const patternLabel = (!isEn() && row.patternKind && te(patternKey)) ? t(patternKey) : row.patternLabel
          const patternLine = row.patternLabel
            ? `<span style="color:${row.patternColor}">${t('zones.strategic.pattern')} · ${patternLabel}</span>`
            : ''
          return [
            `<b>${row.zone}</b>`,
            `<span style="color:${meta.color}">${t('zones.strategic.role')} · ${meta.icon} ${quadrantText(row.quadrant, 'label')}</span>`,
            patternLine,
            '<hr style="margin:6px 0;opacity:.25"/>',
            `${t('zones.strategic.realized')} &nbsp; <b>${fmtMoney(row.eurPerVisitor)}</b>`,
            `${t('zones.strategic.ceiling')} &nbsp; ${fmtMoney(row.ceilingEurPerVisitor)}`,
            `${t('zones.strategic.headroom')} &nbsp; <b>${t('zones.strategic.perVisitor', { v: fmtMoney(row.headroom) })}</b>`,
            '<hr style="margin:6px 0;opacity:.25"/>',
            `${t('zones.strategic.share')} &nbsp; ${isEn() ? `${row.visitorShare.toFixed(1)}%` : formatPercent(row.visitorShare, { decimals: 1 })}`,
            `${t('zones.strategic.visitors')} &nbsp; ${isEn() ? row.visitors.toLocaleString() : formatNumber(row.visitors)}`,
            `${t('zones.strategic.engagement')} &nbsp; ${fmtPct(row.engagementRate)}`,
            `${t('zones.strategic.conversion')} &nbsp; ${fmtPct(row.conversionRate)}`,
            `${t('zones.strategic.avgValue')} &nbsp; ${fmtMoney(row.avgValue)}`,
          ].filter(Boolean).join('<br/>')
        },
      },
      // Axis names use 'middle' positioning to mirror PatternMatrix —
      // centered below the X axis, rotated to the left of the Y
      // axis. Same nameGap values too so both charts share visual
      // rhythm side-by-side.
      xAxis: {
        name: t('zones.strategic.xAxis'),
        nameLocation: 'middle',
        nameGap: 36,
        nameTextStyle: { color: '#6b7a87', fontSize: 12, fontWeight: 500 },
        min: 0,
        max: xMax.value,
        axisLabel: { formatter: (v) => (isEn() ? v + '%' : formatPercent(v, { decimals: decimalsOf(v) })), margin: 10, hideOverlap: true },
        splitLine: { show: true, lineStyle: { color: '#E5EDF6' } },
      },
      yAxis: {
        name: t('zones.strategic.yAxis'),
        nameLocation: 'middle',
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { color: '#6b7a87', fontSize: 12, fontWeight: 500 },
        min: 0,
        max: yMax.value,
        axisLabel: { formatter: (v) => (isEn() ? '€' + v : formatEuro(v, { decimals: decimalsOf(v) })), margin: 10, hideOverlap: true },
        splitLine: { show: true, lineStyle: { color: '#E5EDF6' } },
      },
      // legend intentionally omitted — rendered as HTML below the chart.
      series: [...series.value, medianGuides.value],
    }))

    return { hasData, option, legendItems }
  },
}
</script>

<style scoped>
.sp-shell {
  background: #fff;
  border: 1px solid #e6edf3;
  border-radius: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03);
  padding: 18px 18px 6px;
  margin-bottom: 24px;
}

/* HTML legend below the chart — wraps tight under the x-axis (no canvas
   reserved-space gap) and lets the card grow with the number of lines. */
.chart-legend{
  list-style: none;
  margin: 6px 0 8px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 14px;
  font-size: 11px;
  color: #7a8895;
}
.chart-legend li{ display: inline-flex; align-items: center; gap: 5px; }
.legend-dot{ width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }

.sp-caption {
  font-size: 12px;
  color: #6b7a87;
  line-height: 1.5;
  margin: 0 0 8px;
}

.sp-empty {
  color: #9aa7b3;
  text-align: center;
  padding: 32px 0;
  font-style: italic;
}
</style>
